import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";
import {
  ArrowRight, Check, Calendar, TrendingDown, FlaskConical, Users, CreditCard,
  ShieldCheck, Lock, Zap, ChevronDown, Sparkles, CalendarClock, AlertTriangle,
  Clock, PiggyBank, Link2, RefreshCw,
} from "lucide-react";

const ASSUMED_APR = 21.9;
const fmt0 = (n) => "$" + Math.round(n).toLocaleString("en-US");

// Client-side estimate: month-by-month on a blended APR. Minimum payment uses the standard
// card formula (this month's interest + 1% of balance, floor $25) so payoff terms stay realistic.
function estimatePayoff(balance, apr, extra) {
  const r = apr / 100 / 12;
  let bal = balance, interest = 0, months = 0;
  while (bal > 0.5 && months < 480) {
    const i = bal * r;
    const minPay = Math.max(i + bal * 0.01, 25);
    let pay = minPay + extra;
    if (pay > bal + i) pay = bal + i;
    interest += i;
    bal = bal + i - pay;
    months += 1;
  }
  return { months, interest };
}

function useCountUp(target, ms = 1100) {
  const [val, setVal] = useState(0);
  const ref = useRef();
  useEffect(() => {
    cancelAnimationFrame(ref.current);
    const start = performance.now();
    const from = 0;
    const tick = (now) => {
      const p = Math.min((now - start) / ms, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(from + (target - from) * eased);
      if (p < 1) ref.current = requestAnimationFrame(tick);
    };
    ref.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(ref.current);
  }, [target, ms]);
  return val;
}

function monthsToDate(months) {
  const d = new Date();
  d.setMonth(d.getMonth() + Math.max(months, 0));
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { user, loading } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);

  // Hero teaser (fixed sample) + animated interest-saved number
  const heroSample = useMemo(() => {
    const base = estimatePayoff(14200, ASSUMED_APR, 0);
    const plan = estimatePayoff(14200, ASSUMED_APR, 300);
    return { saved: Math.max(base.interest - plan.interest, 0), date: monthsToDate(plan.months) };
  }, []);
  const heroSaved = useCountUp(heroSample.saved);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-teal-500/30 border-t-teal-500 animate-spin" /></div>;
  if (user) return <Navigate to="/dashboard" replace />;

  const openAuth = () => setAuthOpen(true);


  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-x-hidden" data-testid="landing-page" style={{ scrollBehavior: "smooth" }}>
      <div className="absolute inset-0 bf-grid opacity-20 pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-[900px] bf-glow pointer-events-none" />

      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-background/70 border-b border-border/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="OWEverit" className="h-9 w-9" data-testid="brand-logo" />
            <div><div className="font-display font-extrabold text-lg tracking-tight leading-none">OWEverit</div><div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Every balance, gone</div></div>
          </div>
          <button onClick={openAuth} data-testid="nav-login-btn" className="px-4 py-2 rounded-full border border-border text-sm font-semibold hover:bg-accent transition">Sign in</button>
        </div>
      </header>

      {/* 1. HERO */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-14 pb-16 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-teal-500/30 bg-teal-500/10 text-teal-400 text-[11px] font-bold uppercase tracking-[0.2em] fade-up">
          Free forever · No card · No bank linking
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-6 fade-up" style={{ animationDelay: "60ms" }}>
          Stop guessing what to pay.<br />
          <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-indigo-400 bg-clip-text text-transparent">Get your exact debt-free date.</span>
        </h1>
        <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mt-6 fade-up" style={{ animationDelay: "120ms" }}>
          Ditch the spreadsheets! OWEverit puts your bills and debts in one place and shows you exactly where to focus each month — so you spend less time managing money and more time paying it down. No bank linking, ever. Your numbers stay yours!
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 fade-up" style={{ animationDelay: "180ms" }}>
          <button onClick={openAuth} data-testid="google-login-btn" className="group flex items-center gap-3 px-6 py-3.5 rounded-full bg-white text-slate-900 font-semibold shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all">
            Calculate my debt-free date — free <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <a href="#how-it-works" className="flex items-center gap-2 px-5 py-3.5 rounded-full border border-border text-sm font-semibold hover:bg-accent transition" data-testid="hero-secondary-cta">See how it works <ChevronDown className="h-4 w-4" /></a>
        </div>
        <div className="mt-4 text-xs text-muted-foreground">Takes 60 seconds. Bring your partner along for free.</div>

        {/* hero screenshot + floating outcome card */}
        <div className="relative mt-14 fade-up" style={{ animationDelay: "260ms" }}>
          <div className="absolute -inset-x-10 top-10 h-64 bg-gradient-to-r from-teal-500/20 via-indigo-500/20 to-rose-500/10 blur-3xl pointer-events-none" />
          <Shot src="/shots/dashboard.png" alt="OWEverit dashboard showing KPIs, upcoming bills and payoff progress" testid="hero-shot" className="max-w-5xl mx-auto" />
          <div className="hidden md:block absolute -right-2 lg:-right-8 top-1/2 -translate-y-1/2 w-64 rounded-2xl border border-teal-500/30 bg-card/90 backdrop-blur-xl shadow-2xl p-5 text-left" data-testid="hero-outcome-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-teal-400"><Sparkles className="h-3.5 w-3.5" /> Your plan preview</div>
            <div className="mt-3 text-xs text-muted-foreground">Interest you'd stop paying</div>
            <div className="font-display font-mono font-extrabold text-3xl text-emerald-400">{fmt0(heroSaved)}</div>
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"><CalendarClock className="h-3.5 w-3.5 text-indigo-400" /> Debt-free by <span className="font-semibold text-foreground">{heroSample.date}</span></div>
          </div>
        </div>
      </section>

      {/* 2. AGITATION */}
      <section className="relative z-10 border-y border-border/60 bg-card/40 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] uppercase tracking-[0.2em] text-rose-400 font-bold">The real problem</div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Why most payoff plans quietly fail by month three</h2>
            <p className="text-base text-muted-foreground mt-4">You're not bad with money. Five banking apps, due dates that jump around, and interest eating most of your payment make it impossible to keep a plan in your head or a spreadsheet.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            <PainCard icon={CalendarClock} title="Surprise weekend due dates" body="Weekend and holiday dates slide back to Friday without warning — hello, late fees and a dinged credit score." />
            <PainCard icon={TrendingDown} title="Minimum-payment quicksand" body="Pay only minimums and you stay in debt 15+ years while interest quietly doubles what you borrowed." />
            <PainCard icon={Clock} title="Spreadsheet fatigue" body="One missed row breaks the formulas. Two weeks later you've abandoned it and you're flying blind again." />
          </div>
        </div>
      </section>

      {/* 3. OUTCOMES */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-24 space-y-28">
        <Outcome
          eyebrow="Outcome 01 · Total visibility" icon={Calendar} tone="teal"
          title="Never get blindsided by a due date again"
          body="Every bill, card and loan lands on one wall calendar. Weekend and bank-holiday dates auto-shift to the prior business day, recurring bills project forward until you cancel them, and autopay stays quiet — so nothing surprises you."
          bullets={["One month view for everything you owe", "Automatic weekend / holiday shifting", "Recurring bills projected out for you"]}
          shot="/shots/calendar.png" alt="OWEverit wall calendar with color-coded bills and projected debt payments" testid="feature-calendar" />
        <Outcome flip
          eyebrow="Outcome 02 · Interest, eliminated" icon={TrendingDown} tone="indigo"
          title="Know exactly what to pay this month — and watch the balance collapse"
          body="Pick Snowball for fast wins or Avalanche to save the most interest, set how much extra you can spare, and OWEverit hands you an exact 'pay this' list. As each card hits zero, its minimum rolls into the next target automatically."
          bullets={["An exact monthly payment list, not a spreadsheet", "Freed-up minimums roll to the next debt", "Progress bars, streaks and payoff confetti"]}
          shot="/shots/debts.png" alt="OWEverit debt tracker with this month's payment list and progress" testid="feature-debts" />
        <Outcome
          eyebrow="Outcome 03 · Proof before you commit" icon={FlaskConical} tone="violet"
          title="See your debt-free date move before you spend a dollar"
          body="The simulator shows your payoff date and total interest for every strategy, and the utilization lab shows what to pay on each card to slip under 30% before the statement reports to the bureaus."
          bullets={["Payoff date + total interest, side by side", "Per-debt payoff timeline", "Credit-utilization lab with per-card control"]}
          shot="/shots/simulator.png" alt="OWEverit payoff simulator charts" testid="feature-simulator" />
        <Outcome flip
          eyebrow="Outcome 04 · Better together" icon={Users} tone="rose"
          title="Run the whole household from one plan, on two phones"
          body="Invite your partner with a private link and you both see the same bills, calendar, debts and plan in real time. Either of you logs a payment in one tap — no double-paying, no 'did you pay it?' texts."
          bullets={["Private invite links you control", "Shared bills, debts, plan and history", "Instant sync across both phones"]}
          custom={<HouseholdMock />} testid="feature-household" />
      </section>

      {/* 4. INTERACTIVE PAYOFF TEASER */}
      <PayoffTeaser onStart={openAuth} />

      {/* 5. TRUST */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.2em] text-teal-400 font-bold">Peace of mind</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Your money data stays strictly yours</h2>
          <p className="text-base text-muted-foreground mt-4">OWEverit is a planner, not a bank connection. No online-banking passwords, no card numbers, no ability to move a cent.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-12">
          <Trust icon={Lock} title="No bank login, ever" body="You enter and update balances yourself. We never touch bank APIs or your credentials." />
          <Trust icon={ShieldCheck} title="Private by default" body="Your plan is visible only to you and the household members you explicitly invite." />
          <Trust icon={Zap} title="60-second start" body="Sign in with email or Google and build your first payoff calendar in under a minute." />
        </div>
      </section>

      {/* 6. HOW IT WORKS */}
      <section id="how-it-works" className="relative z-10 bg-card/40 border-y border-border/60">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center">
            <div className="text-[11px] uppercase tracking-[0.2em] text-teal-400 font-bold">How it works</div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Three steps to a plan you'll actually follow</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <Step n="01" icon={CreditCard} title="Add bills, cards & loans" body="Balances, APRs, due dates and credit limits. Under 3 minutes — zero-balance cards count for utilization too." />
            <Step n="02" icon={Zap} title="Pick a strategy & surplus" body="Snowball for momentum, Avalanche for the math. Set your extra and see your payoff date instantly." />
            <Step n="03" icon={Check} title="Pay what the list says" body="Log each payment in one tap. Balances, utilization, calendar and your debt-free date update live." />
          </div>
        </div>
      </section>

      {/* 7. FAQ */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-20">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-center">Frequently asked questions</h2>
        <div className="mt-8 divide-y divide-border/60 rounded-2xl border border-border bg-card">
          <Faq q="Is OWEverit really completely free?" a="Yes. Every core feature — the calendar, debt tracker, simulator lab and household sharing — is free with no credit card required." />
          <Faq q="Does it connect to my bank?" a="No. You enter balances and log payments yourself. Nothing is linked and nothing can be moved — it's purely planning, so your credentials stay with you." />
          <Faq q="Why not just use a spreadsheet?" a="Spreadsheets don't shift due dates around holidays, recompute interest as you pay, celebrate payoffs, or sync instantly to your partner's phone. OWEverit does all of it automatically." />
          <Faq q="Snowball or Avalanche — which should I pick?" a="Avalanche (highest APR first) saves the most interest; Snowball (smallest balance first) gives quicker wins. The simulator shows both with real numbers so you can decide with confidence." />
          <Faq q="Can my partner see and pay bills too?" a="Yes. Create a household, share the private invite link, and you both manage the exact same plan in real time." />
        </div>
      </section>

      {/* 8. FINAL CTA */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pb-24">
        <div className="relative overflow-hidden rounded-3xl border border-teal-500/30 p-10 md:p-14 text-center" style={{ background: "linear-gradient(135deg,#0B1220 0%,#1E1B4B 100%)" }}>
          <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-teal-500/30 blur-3xl" />
          <div className="absolute -bottom-20 -left-16 h-56 w-56 rounded-full bg-indigo-500/30 blur-3xl" />
          <h2 className="relative font-display text-3xl md:text-4xl font-extrabold tracking-tight text-white">Take the guesswork out of your debt tonight.</h2>
          <p className="relative text-slate-300 mt-3 max-w-xl mx-auto text-sm md:text-base">Add your bills, pick a strategy, and wake up to a calendar that knows what's due and a plan that knows what to pay.</p>
          <button onClick={openAuth} data-testid="final-login-btn" className="relative mt-8 inline-flex items-center gap-3 px-7 py-4 rounded-full bg-white text-slate-900 font-semibold shadow-xl hover:-translate-y-0.5 transition-all">
            Start free — takes 60 seconds <ArrowRight className="h-4 w-4" />
          </button>
          <div className="relative mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] text-slate-400">
            <span className="inline-flex items-center gap-1"><Check className="h-3.5 w-3.5 text-emerald-400" /> 100% free forever</span>
            <span className="inline-flex items-center gap-1"><Check className="h-3.5 w-3.5 text-emerald-400" /> No credit card</span>
            <span className="inline-flex items-center gap-1"><Check className="h-3.5 w-3.5 text-emerald-400" /> No bank linking</span>
          </div>
        </div>
        <div className="text-center text-[11px] text-muted-foreground mt-8">© {new Date().getFullYear()} OWEverit · Every balance, gone</div>
      </section>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
}

function PayoffTeaser({ onStart }) {
  const [balance, setBalance] = useState(12000);
  const [extra, setExtra] = useState(250);
  const est = useMemo(() => {
    const base = estimatePayoff(balance, ASSUMED_APR, 0);
    const plan = estimatePayoff(balance, ASSUMED_APR, extra);
    return {
      saved: Math.max(base.interest - plan.interest, 0),
      monthsSaved: Math.max(base.months - plan.months, 0),
      date: monthsToDate(plan.months),
    };
  }, [balance, extra]);

  return (
    <section className="relative z-10 bg-card/40 border-y border-border/60">
      <div className="max-w-5xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.2em] text-indigo-400 font-bold">Try it right now</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">See what an optimized plan saves you</h2>
          <p className="text-base text-muted-foreground mt-4">Drag the sliders to match your situation. This is a quick estimate — your real plan uses your exact balances and APRs.</p>
        </div>

        <div className="mt-10 grid lg:grid-cols-2 gap-8 items-center rounded-3xl border border-border bg-card p-6 md:p-8" data-testid="payoff-teaser">
          <div className="space-y-8">
            <Slider label="Total debt balance" value={balance} min={1000} max={50000} step={500}
              display={fmt0(balance)} onChange={setBalance} testid="teaser-balance" />
            <Slider label="Extra you can pay each month" value={extra} min={0} max={1000} step={25}
              display={fmt0(extra) + "/mo"} onChange={setExtra} testid="teaser-extra" />
            <div className="text-[11px] text-muted-foreground">Assumes a blended {ASSUMED_APR}% APR and typical minimum payments (interest + 1% of the balance). For illustration only.</div>
          </div>
          <div className="rounded-2xl border border-teal-500/25 bg-gradient-to-br from-teal-500/10 to-indigo-500/10 p-6 text-center">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-teal-400">Interest you'd avoid</div>
            <div className="font-display font-mono font-extrabold text-4xl md:text-5xl text-emerald-400 mt-2" data-testid="teaser-saved">{fmt0(est.saved)}</div>
            <div className="mt-5 grid grid-cols-2 gap-4">
              <div className="rounded-xl bg-background/60 border border-border/60 p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Debt-free by</div>
                <div className="font-display font-bold text-lg mt-1" data-testid="teaser-date">{est.date}</div>
              </div>
              <div className="rounded-xl bg-background/60 border border-border/60 p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Sooner by</div>
                <div className="font-display font-bold text-lg mt-1">{est.monthsSaved} mo</div>
              </div>
            </div>
            <button onClick={onStart} data-testid="teaser-calculator-cta" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-slate-900 font-semibold shadow-lg hover:-translate-y-0.5 transition-all">
              <PiggyBank className="h-4 w-4" /> Build my full plan — free
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Slider({ label, value, min, max, step, display, onChange, testid }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-sm font-semibold">{label}</label>
        <span className="font-display font-mono font-bold text-teal-400">{display}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} data-testid={testid}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-3 w-full h-2 rounded-full appearance-none cursor-pointer teaser-range"
        style={{ background: `linear-gradient(90deg,#14B8A6 ${pct}%, hsl(var(--border)) ${pct}%)` }} />
    </div>
  );
}

function Shot({ src, alt, testid, className = "" }) {
  return (
    <div className={`relative rounded-2xl border border-border/80 bg-card shadow-2xl overflow-hidden ${className}`} data-testid={testid}>
      <div className="h-8 flex items-center gap-1.5 px-3 border-b border-border/60 bg-accent/40">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-500/70" /><span className="h-2.5 w-2.5 rounded-full bg-amber-500/70" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
      </div>
      <img src={src} alt={alt} loading="lazy" className="w-full block" />
    </div>
  );
}

function PainCard({ icon: Icon, title, body }) {
  return (
    <div className="p-6 rounded-2xl border border-border bg-background/40">
      <div className="h-10 w-10 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-400"><Icon className="h-5 w-5" /></div>
      <div className="font-display font-bold text-lg mt-4">{title}</div>
      <p className="text-sm text-muted-foreground mt-2">{body}</p>
    </div>
  );
}

function Outcome({ eyebrow, icon: Icon, tone, title, body, bullets, shot, alt, flip, testid, custom }) {
  const tones = { teal: "text-teal-400 bg-teal-500/10 border-teal-500/30", indigo: "text-indigo-400 bg-indigo-500/10 border-indigo-500/30", violet: "text-violet-400 bg-violet-500/10 border-violet-500/30", rose: "text-rose-400 bg-rose-500/10 border-rose-500/30" };
  return (
    <div className={`grid lg:grid-cols-12 gap-10 items-center ${flip ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={testid}>
      <div className="lg:col-span-5">
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] font-bold uppercase tracking-[0.2em] ${tones[tone]}`}><Icon className="h-3.5 w-3.5" /> {eyebrow}</div>
        <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-4">{title}</h3>
        <p className="text-sm md:text-base text-muted-foreground mt-4">{body}</p>
        <ul className="mt-5 space-y-2">{bullets.map(b => <li key={b} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {b}</li>)}</ul>
      </div>
      <div className="lg:col-span-7">{custom || <Shot src={shot} alt={alt} />}</div>
    </div>
  );
}

function HouseholdMock() {
  const members = [
    { name: "Alex", email: "alex@home.com", role: "Owner", initial: "A", ring: "ring-teal-400/60", bg: "bg-teal-500/20 text-teal-300" },
    { name: "Sam", email: "sam@home.com", role: "Member", initial: "S", ring: "ring-indigo-400/60", bg: "bg-indigo-500/20 text-indigo-300" },
  ];
  return (
    <div className="relative rounded-2xl border border-border/80 bg-card shadow-2xl overflow-hidden">
      <div className="h-8 flex items-center gap-1.5 px-3 border-b border-border/60 bg-accent/40">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-500/70" /><span className="h-2.5 w-2.5 rounded-full bg-amber-500/70" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
        <span className="ml-2 text-[10px] text-muted-foreground">oweverit.com/household</span>
      </div>
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-indigo-400">Our household</div>
            <div className="font-display font-bold text-xl mt-0.5">The Rivera Home</div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-[10px] font-bold uppercase tracking-wider">
            <RefreshCw className="h-3 w-3" /> Synced
          </span>
        </div>

        {/* members */}
        <div className="mt-5 space-y-2">
          {members.map(m => (
            <div key={m.name} className="flex items-center gap-3 p-3 rounded-xl border border-border/60 bg-background/50">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center font-bold ring-2 ${m.ring} ${m.bg}`}>{m.initial}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm flex items-center gap-2">{m.name}
                  <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${m.role === "Owner" ? "bg-amber-500/15 text-amber-400 border border-amber-500/30" : "bg-white/5 text-muted-foreground border border-border/60"}`}>{m.role}</span>
                </div>
                <div className="text-[11px] text-muted-foreground truncate">{m.email}</div>
              </div>
              <Check className="h-4 w-4 text-emerald-500" />
            </div>
          ))}
        </div>

        {/* invite link */}
        <div className="mt-4 p-3 rounded-xl border border-indigo-500/25 bg-indigo-500/5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-indigo-400"><Link2 className="h-3.5 w-3.5" /> Private invite link</div>
          <div className="mt-1.5 flex items-center gap-2">
            <code className="flex-1 truncate text-[11px] font-mono text-muted-foreground bg-background/60 rounded-md px-2 py-1.5 border border-border/60">oweverit.com/join/9f3a-partner</code>
            <span className="px-2.5 py-1.5 rounded-md bg-indigo-500 text-white text-[11px] font-semibold">Copy</span>
          </div>
        </div>

        {/* shared plan footer */}
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          {[["Shared bills", "18"], ["Shared debts", "4"], ["Both can pay", "✓"]].map(([k, v]) => (
            <div key={k} className="rounded-xl bg-background/50 border border-border/60 py-2.5">
              <div className="font-display font-mono font-extrabold text-lg">{v}</div>
              <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{k}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Trust({ icon: Icon, title, body }) {
  return (
    <div className="p-6 rounded-2xl border border-border bg-card">
      <div className="h-10 w-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400"><Icon className="h-5 w-5" /></div>
      <div className="font-display font-bold text-lg mt-4">{title}</div>
      <p className="text-sm text-muted-foreground mt-2">{body}</p>
    </div>
  );
}

function Step({ n, icon: Icon, title, body }) {
  return (
    <div className="p-6 rounded-2xl border border-border bg-card relative">
      <div className="absolute -top-3 left-6 h-7 px-2 rounded-full bg-teal-500 text-white text-xs font-bold flex items-center justify-center font-mono">{n}</div>
      <Icon className="h-5 w-5 text-teal-400 mt-2" />
      <div className="font-display font-bold text-lg mt-3">{title}</div>
      <p className="text-sm text-muted-foreground mt-2">{body}</p>
    </div>
  );
}

function Faq({ q, a }) {
  return (
    <details className="group p-5">
      <summary className="flex items-center justify-between cursor-pointer list-none font-semibold text-sm md:text-base">{q}<ChevronDown className="h-4 w-4 text-muted-foreground group-open:rotate-180 transition-transform" /></summary>
      <p className="text-sm text-muted-foreground mt-3">{a}</p>
    </details>
  );
}
